import { Vector2, CharacterStats, ElementType } from '@shared/types';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { StatusEffectManager, StatusEffectConfig, StatusType } from '@shared/effects/StatusEffect';
import { BuffManager, BuffType } from '@shared/effects/BuffSystem';
import { COMBAT } from '@shared/constants';

export interface EntityConfig {
  x: number;
  y: number;
  width: number;
  height: number;
  speed: number;
  stats: CharacterStats;
}

export abstract class Entity {
  public id: string;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public width: number;
  public height: number;
  public speed: number;
  public stats: CharacterStats;
  public isAlive: boolean = true;
  public lastDamageSourceName: string = '';
  public facing: 'up' | 'down' | 'left' | 'right' | 'up-left' | 'up-right' | 'down-left' | 'down-right' = 'down';

  // Combat state
  public isAttacking: boolean = false;
  public attackCooldown: number = 0;
  public invincible: boolean = false;
  public invincibleTimer: number = 0;

  // Status effects
  public statusEffects: StatusEffectManager = new StatusEffectManager();
  public isFrozen: boolean = false;
  public slowMultiplier: number = 1.0; // 1.0 = normal speed, 0.5 = 50% slower
  public damageAmplify: number = 0; // % increased damage taken

  // Buff system
  public buffManager: BuffManager = new BuffManager();
  public isSilenced: boolean = false;

  constructor(config: EntityConfig) {
    this.id = this.generateId();
    this.position = { x: config.x, y: config.y };
    this.width = config.width;
    this.height = config.height;
    this.speed = config.speed;
    this.stats = { ...config.stats };
  }

  private generateId(): string {
    return `entity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  abstract update(deltaTime: number): void;
  abstract render(renderer: Renderer, camera: Camera): void;

  /** Override in subclasses to provide actual level */
  getLevel(): number {
    return 1;
  }

  move(dx: number, dy: number, deltaTime: number): void {
    if (!this.isAlive) return;

    // Cannot move while frozen
    if (this.isFrozen) {
      this.velocity.x = 0;
      this.velocity.y = 0;
      return;
    }

    // Normalize diagonal movement
    if (dx !== 0 && dy !== 0) {
      const length = Math.sqrt(dx * dx + dy * dy);
      dx /= length;
      dy /= length;
    }

    // Apply slow effect to speed
    const effectiveSpeed = this.speed * this.slowMultiplier;

    this.velocity.x = dx * effectiveSpeed;
    this.velocity.y = dy * effectiveSpeed;

    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;

    // Update facing direction (8-directional)
    if (dx > 0 && dy > 0) this.facing = 'down-right';
    else if (dx > 0 && dy < 0) this.facing = 'up-right';
    else if (dx < 0 && dy > 0) this.facing = 'down-left';
    else if (dx < 0 && dy < 0) this.facing = 'up-left';
    else if (dx > 0) this.facing = 'right';
    else if (dx < 0) this.facing = 'left';
    else if (dy > 0) this.facing = 'down';
    else if (dy < 0) this.facing = 'up';
  }

  /**
   * Get the facing direction as an angle in radians.
   * 0 = right, PI/2 = down, PI = left, -PI/2 = up
   */
  getFacingAngle(): number {
    switch (this.facing) {
      case 'right': return 0;
      case 'down-right': return Math.PI / 4;
      case 'down': return Math.PI / 2;
      case 'down-left': return Math.PI * 3 / 4;
      case 'left': return Math.PI;
      case 'up-left': return -Math.PI * 3 / 4;
      case 'up': return -Math.PI / 2;
      case 'up-right': return -Math.PI / 4;
      default: return Math.PI / 2; // default down
    }
  }

  /**
   * Get the facing direction as a unit vector.
   */
  getFacingVector(): Vector2 {
    const angle = this.getFacingAngle();
    return { x: Math.cos(angle), y: Math.sin(angle) };
  }

  /**
   * Apply a status effect to this entity
   */
  applyStatusEffect(config: StatusEffectConfig, sourceId: string): void {
    if (!this.isAlive) return;
    this.statusEffects.apply(config, sourceId);
  }

  /**
   * Remove a status effect from this entity
   */
  removeStatusEffect(type: StatusType): boolean {
    return this.statusEffects.remove(type);
  }

  /**
   * Check if entity has a specific status effect
   */
  hasStatusEffect(type: StatusType): boolean {
    return this.statusEffects.has(type);
  }

  /**
   * Update status effects and return DoT damage
   */
  protected updateStatusEffects(deltaTime: number): number {
    const isMoving = this.velocity.x !== 0 || this.velocity.y !== 0;
    const result = this.statusEffects.update(deltaTime, isMoving);

    // Update entity state from status effects
    this.isFrozen = result.isFrozen;
    this.slowMultiplier = Math.max(0, 1.0 - Math.min(100, result.slowPercent) / 100);
    this.damageAmplify = result.damageAmplify;

    return result.damage;
  }

  /**
   * Update buffs and return regen amounts
   */
  protected updateBuffs(deltaTime: number): { hpRegen: number; manaRegen: number; expiredBuffs: string[] } {
    const result = this.buffManager.update(deltaTime);

    // Update silence state
    this.isSilenced = result.isSilenced;

    // Check for invulnerability
    if (this.buffManager.isInvulnerable()) {
      this.invincible = true;
    }

    return {
      hpRegen: result.hpRegen,
      manaRegen: result.manaRegen,
      expiredBuffs: result.expiredBuffs,
    };
  }

  /**
   * Apply a buff by template ID
   */
  applyBuff(templateId: string, sourceId: string, sourceType: 'skill' | 'item' | 'potion' | 'aura' | 'trigger' | 'enemy' = 'skill'): void {
    this.buffManager.applyBuff(templateId, sourceId, sourceType);
  }

  /**
   * Remove a buff by type
   */
  removeBuff(type: BuffType, sourceId?: string): boolean {
    return this.buffManager.removeBuff(type, sourceId);
  }

  /**
   * Check if entity has a specific buff
   */
  hasBuff(type: BuffType): boolean {
    return this.buffManager.hasBuff(type);
  }

  /**
   * Clear all debuffs
   */
  clearDebuffs(): void {
    this.buffManager.clearDebuffs();
    this.statusEffects.clear();
  }

  takeDamage(amount: number, type: ElementType = 'physical', _sourceOrBypass?: any): number {
    if (!this.isAlive || this.invincible) return 0;

    // Check if ES should be bypassed (karanlik damage)
    const bypassES = typeof _sourceOrBypass === 'boolean' ? _sourceOrBypass : false;

    // Apply damage amplification from status effects (shock)
    if (this.damageAmplify > 0) {
      amount = amount * (1 + this.damageAmplify / 100);
    }

    let finalDamage = Math.floor(amount);

    // Apply armor for physical damage
    if (type === 'physical' && this.stats.armor > 0) {
      // Formula: reduction = armor / (armor + ARMOR_CONSTANT * level)
      const level = this.getLevel();
      const reduction = this.stats.armor / (this.stats.armor + COMBAT.ARMOR_CONSTANT * level);
      finalDamage = Math.floor(amount * (1 - Math.min(reduction, COMBAT.MAX_ARMOR_REDUCTION)));
    }

    // Apply energy shield first (unless bypassES is true, e.g. karanlik damage)
    if (this.stats.energyShield > 0 && !bypassES) {
      if (this.stats.energyShield >= finalDamage) {
        this.stats.energyShield -= finalDamage;
        return finalDamage;
      } else {
        finalDamage -= this.stats.energyShield;
        this.stats.energyShield = 0;
      }
    }

    // Apply to HP
    this.stats.hp = Math.max(0, this.stats.hp - finalDamage);

    if (this.stats.hp <= 0) {
      this.die();
    }

    return finalDamage;
  }

  heal(amount: number): void {
    if (!this.isAlive) return;
    this.stats.hp = Math.min(this.stats.maxHp, this.stats.hp + Math.floor(amount));
  }

  die(): void {
    this.isAlive = false;
    this.velocity = { x: 0, y: 0 };
  }

  setInvincible(duration: number): void {
    this.invincible = true;
    this.invincibleTimer = duration;
  }

  public updateTimers(deltaTime: number): void {
    // Update attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= deltaTime;
      if (this.attackCooldown <= 0) {
        this.attackCooldown = 0;
        this.isAttacking = false;
      }
    }

    // Update invincibility
    if (this.invincible && this.invincibleTimer > 0) {
      this.invincibleTimer -= deltaTime;
      if (this.invincibleTimer <= 0) {
        this.invincible = false;
        this.invincibleTimer = 0;
      }
    }
  }

  // Collision detection
  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x - this.width / 2,
      y: this.position.y - this.height / 2,
      width: this.width,
      height: this.height,
    };
  }

  /**
   * Get hitbox radius for circular collision detection
   * Uses average of width/height for a reasonable approximation
   */
  getHitboxRadius(): number {
    return (this.width + this.height) / 4;
  }

  collidesWith(other: Entity): boolean {
    const a = this.getBounds();
    const b = other.getBounds();

    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  distanceTo(other: Entity): number {
    const dx = other.position.x - this.position.x;
    const dy = other.position.y - this.position.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  distanceToSquared(other: Entity): number {
    const dx = other.position.x - this.position.x;
    const dy = other.position.y - this.position.y;
    return dx * dx + dy * dy;
  }

  directionTo(other: Entity): Vector2 {
    const dx = other.position.x - this.position.x;
    const dy = other.position.y - this.position.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) return { x: 0, y: 0 };

    return { x: dx / length, y: dy / length };
  }

  // Get screen position (for isometric rendering)
  getScreenPosition(camera: Camera): Vector2 {
    return camera.worldToScreen(this.position.x, this.position.y);
  }

  // Health percentage
  getHealthPercent(): number {
    return this.stats.maxHp > 0 ? this.stats.hp / this.stats.maxHp : 0;
  }

  getManaPercent(): number {
    return this.stats.maxMana > 0 ? this.stats.mana / this.stats.maxMana : 0;
  }

  getEnergyShieldPercent(): number {
    if (this.stats.maxEnergyShield === 0) return 0;
    return this.stats.energyShield / this.stats.maxEnergyShield;
  }
}
